import { Button, Chip, Input, useDialog } from '@emailforge/ui';
import { useState } from 'react';

import { TopBar } from '@/components/layout/TopBar';
import { Icon } from '@/components/ui/icons';
import {
  type TrackingDomain,
  useCreateTrackingDomainMutation,
  useDeleteTrackingDomainMutation,
  useListTrackingDomainsQuery,
  useRenewTrackingDomainCertMutation,
  useVerifyTrackingDomainMutation,
  type VerifyResult,
} from '@/features/tracking-domains/trackingDomainsApi';
import { t } from '@/lib/i18n';

export function TrackingDomainsScreen() {
  const { data, isLoading } = useListTrackingDomainsQuery();
  const [create, createMut] = useCreateTrackingDomainMutation();
  const [domain, setDomain] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    const value = domain.trim().toLowerCase();
    if (!/^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/.test(value)) {
      setError('Enter a valid domain like links.your-brand.com');
      return;
    }
    try {
      await create({ domain: value }).unwrap();
      setDomain('');
    } catch (err: unknown) {
      setError(
        (err as { data?: { message?: string } })?.data?.message ?? 'Could not register domain.',
      );
    }
  };

  return (
    <>
      <TopBar crumbs={['Workspace', t('nav.tracking_domains')]} />
      <div style={{ flex: 1, padding: 24, background: 'var(--paper-2)', overflowY: 'auto' }}>
        <div style={{ maxWidth: 880, margin: '0 auto' }}>
          <div style={{ marginBottom: 24 }}>
            <h1 className="display" style={{ fontSize: 28, margin: '0 0 6px', letterSpacing: -0.01 }}>
              Tracking domains
            </h1>
            <p style={{ fontSize: 13.5, color: 'var(--ink-2)', margin: 0 }}>
              Send opens and click-through links from your own subdomain (e.g.{' '}
              <span className="mono">links.your-brand.com</span>) so recipients see your domain, not ours.
            </p>
          </div>

          <form
            onSubmit={submit}
            className="mf-card"
            style={{ padding: 16, marginBottom: 24, display: 'flex', gap: 8, alignItems: 'center' }}
          >
            <Input
              
              style={{ flex: 1, height: 40 }}
              placeholder="links.your-brand.com"
              value={domain}
              onChange={(e) => setDomain(e.target.value)} />
            <Button variant="flame" type="submit"
              
              disabled={createMut.isLoading || domain.trim().length === 0}>
              <Icon.plus size={13} />
              {createMut.isLoading ? 'Adding…' : 'Add domain'}
            </Button>
          </form>
          {error && (
            <div
              style={{
                padding: 12,
                marginBottom: 20,
                background: 'var(--flame-soft)',
                border: '1px solid var(--flame-line)',
                borderRadius: 'var(--r-2)',
                color: 'var(--flame-2)',
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {isLoading && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>Loading…</div>}

          {!isLoading && (data ?? []).length === 0 && (
            <div
              className="mf-card"
              style={{ padding: 40, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13.5 }}
            >
              No tracking domains yet. Add one above to start sending links from your subdomain.
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {(data ?? []).map((d) => (
              <DomainCard key={d.id} domain={d} />
            ))}
          </div>
        </div>
      </div>
    </>
  );
}

function DomainCard({ domain }: { domain: TrackingDomain }) {
  const [verify, verifyMut] = useVerifyTrackingDomainMutation();
  const [del, delMut] = useDeleteTrackingDomainMutation();
  const [renew, renewMut] = useRenewTrackingDomainCertMutation();
  const [result, setResult] = useState<VerifyResult | null>(null);
  const { confirm } = useDialog();

  const runVerify = async () => {
    setResult(null);
    const r = await verify(domain.id).unwrap();
    setResult(r);
  };

  const runRenew = async () => {
    await renew(domain.id).unwrap();
  };

  const remove = async () => {
    if (!await confirm({
      title: 'Remove tracking domain?',
      description: `${domain.domain} will be removed and the CNAME no longer resolves through us. Already-sent emails keep tracking until they hit the original record.`,
      confirmLabel: 'Remove',
      destructive: true,
    })) return;
    await del(domain.id).unwrap();
  };

  const chip: { label: string; variant: 'live' | 'err' | 'flame' } =
    domain.verificationStatus === 'verified'
      ? { label: 'Verified', variant: 'live' }
      : domain.verificationStatus === 'failed'
        ? { label: 'Failed', variant: 'err' }
        : { label: 'Pending verification', variant: 'flame' };

  return (
    <div className="mf-card" style={{ padding: 18 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 550, marginBottom: 2 }}>{domain.domain}</div>
          <div className="mono" style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            CNAME → {domain.cnameTarget}
          </div>
        </div>
        <Chip variant={chip.variant}>{chip.label}</Chip>
        <Button size="sm" onClick={runVerify} disabled={verifyMut.isLoading}>
          {verifyMut.isLoading ? 'Checking…' : 'Verify'}
        </Button>
        <Button variant="ghost" size="sm" icon aria-label="Delete" onClick={remove} disabled={delMut.isLoading}>
          <Icon.trash size={12} />
        </Button>
      </div>

      {domain.verificationStatus === 'verified' && (
        <div
          style={{
            marginTop: 12,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '10px 12px',
            background: 'var(--paper-2)',
            border: '1px solid var(--line-soft)',
            borderRadius: 'var(--r-2)',
          }}
        >
          <CertChip status={domain.certStatus} expiresAt={domain.certExpiresAt} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="mono" style={{ fontSize: 11, color: 'var(--ink-3)' }}>
              {certCopy(domain)}
            </div>
            {domain.certRenewalError && (
              <div className="mono" style={{ fontSize: 10.5, color: 'var(--err)', marginTop: 2 }}>
                {domain.certRenewalError}
              </div>
            )}
          </div>
          <Button size="sm" onClick={runRenew} disabled={renewMut.isLoading}>
            {renewMut.isLoading ? 'Renewing…' : domain.certStatus === 'pending' ? 'Issue cert' : 'Renew cert'}
          </Button>
        </div>
      )}

      {(result || domain.verificationStatus !== 'verified') && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 14,
            borderTop: '1px solid var(--line-soft)',
            fontSize: 12.5,
            color: 'var(--ink-2)',
          }}
        >
          {result?.verified === false && (
            <div
              style={{
                padding: 10,
                marginBottom: 10,
                background: 'var(--flame-soft)',
                border: '1px solid var(--flame-line)',
                borderRadius: 'var(--r-2)',
                color: 'var(--flame-2)',
              }}
            >
              CNAME didn't resolve{result.resolvedTo ? ` (got ${result.resolvedTo})` : ''}. Expected{' '}
              <span className="mono">{result.expected}</span>.
            </div>
          )}
          {result?.verified === true && (
            <div
              style={{
                padding: 10,
                marginBottom: 10,
                background: 'var(--spark-soft)',
                border: '1px solid var(--spark-line)',
                borderRadius: 'var(--r-2)',
                color: 'oklch(0.40 0.14 148)',
              }}
            >
              <Icon.check size={12} /> Verified — links now route through {domain.domain}.
            </div>
          )}
          <div style={{ fontWeight: 500, marginBottom: 6 }}>Add this DNS record:</div>
          <div
            className="mono"
            style={{
              padding: 10,
              background: 'var(--paper-3)',
              borderRadius: 'var(--r-2)',
              fontSize: 11.5,
              display: 'grid',
              gridTemplateColumns: '60px 1fr 1fr',
              gap: 10,
            }}
          >
            <span style={{ color: 'var(--ink-3)' }}>CNAME</span>
            <span>{domain.domain}</span>
            <span>{domain.cnameTarget}</span>
          </div>
          {result?.instructions && result.instructions.length > 0 && (
            <ul style={{ marginTop: 10, paddingLeft: 18, fontSize: 12 }}>
              {result.instructions.map((ins, i) => (
                <li key={i} style={{ marginBottom: 4 }}>
                  <span className="mono">{ins.type}</span> {ins.host} → {ins.value}
                  {ins.note && <span style={{ color: 'var(--ink-3)' }}> ({ins.note})</span>}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function CertChip({
  status,
  expiresAt,
}: {
  status: TrackingDomain['certStatus'];
  expiresAt: string | null;
}) {
  const expiringSoon =
    expiresAt != null &&
    new Date(expiresAt).getTime() - Date.now() < 30 * 24 * 3600 * 1000;
  if (status === 'issued' && expiringSoon) {
    return <Chip variant="flame">cert · expiring soon</Chip>;
  }
  if (status === 'issued') return <Chip variant="live">cert · issued</Chip>;
  if (status === 'renewing') return <Chip variant="flame">cert · renewing</Chip>;
  if (status === 'failed') return <Chip variant="err">cert · failed</Chip>;
  if (status === 'expired') return <Chip variant="err">cert · expired</Chip>;
  return <Chip>cert · pending</Chip>;
}

function certCopy(domain: TrackingDomain): string {
  if (domain.certStatus === 'pending') {
    return 'No certificate issued yet. Click "Issue cert" once your CNAME is verified.';
  }
  if (domain.certStatus === 'renewing') {
    return 'Certificate renewal in progress…';
  }
  if (domain.certStatus === 'failed') {
    return 'Last renewal attempt failed. Click "Renew cert" to try again.';
  }
  if (domain.certExpiresAt) {
    const expires = new Date(domain.certExpiresAt);
    const days = Math.max(0, Math.round((expires.getTime() - Date.now()) / (24 * 3600 * 1000)));
    return `Expires ${expires.toLocaleDateString()} · ${days} day${days === 1 ? '' : 's'} remaining`;
  }
  return 'Certificate status: ' + domain.certStatus;
}
